import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, PartyPopper, CheckCircle2 } from 'lucide-react';
import { getCollection, getRevisionFamilia, RevisionFamilia, CollectionResp, CollectionStudent, CollectionStatus, moneyEntero as money } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import WhatsAppIcon from '../../../components/WhatsAppIcon';
import EmptyState from '../../../components/EmptyState';
import CountUp from '../../../components/CountUp';
import { Skeleton } from '../../../components/Skeleton';

// 🚨 17-sep-2026: esto era `new Date().toISOString().slice(0,7)`, o sea el mes en UTC. En Mexicali
// (UTC-7), el último día del mes a partir de las 17:00 la pantalla saltaba al mes siguiente y
// aparecía TODO EL MUNDO debiendo. El resto de la app ya usa la fecha local a propósito.
function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftMonth(m: string, delta: number) {
  const [y, mm] = m.split('-').map(Number);
  const d = new Date(y, mm - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(m: string) {
  const [y, mm] = m.split('-').map(Number);
  return new Date(y, mm - 1, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

const STATUS_BADGE: Record<CollectionStatus, { label: string; cls: string }> = {
  pending: { label: 'Sin pagar', cls: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  partial: { label: 'Abonó', cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  paid: { label: 'Al corriente', cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
};

type Filter = 'pendientes' | 'parciales' | 'pagadas' | 'todas';
const FILTERS: [Filter, string][] = [['pendientes', 'Por cobrar'], ['parciales', 'Abonos'], ['pagadas', 'Al corriente'], ['todas', 'Todas']];

function waMessage(s: CollectionStudent, mLabel: string) {
  const nombre = s.student_name.split(' ')[0] || '';
  const monto = money(s.status === 'partial' ? s.remaining : s.expected);
  return `Hola ${nombre} 👋, te recordamos tu mensualidad de ${mLabel} en Let's Move (${s.membership_name}): ${monto}. ¡Gracias!`;
}

export default function CollectionPanel() {
  const [month, setMonth] = useState(thisMonth());
  const [resp, setResp] = useState<CollectionResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pendientes');

  const reload = useCallback(() => {
    setLoading(true);
    getCollection(month).then(setResp).finally(() => setLoading(false));
  }, [month]);
  useEffect(() => { reload(); }, [reload]);
  // Descuentos de FAMILIA a revisar este mes (23-sep-2026): sin familia registrada o sin familiar activo.
  const [revFam, setRevFam] = useState<RevisionFamilia[]>([]);
  useEffect(() => { getRevisionFamilia(month).then((r) => setRevFam(r.revisar || [])).catch(() => setRevFam([])); }, [month]);

  const mLabel = monthLabel(month);
  const isCurrent = month === thisMonth();

  const rows = useMemo(() => {
    if (!resp) return [];
    return resp.data.filter((s) =>
      filter === 'todas' ? true
        : filter === 'pendientes' ? s.status !== 'paid'
        : filter === 'parciales' ? s.status === 'partial'
        : s.status === 'paid');
  }, [resp, filter]);

  function exportCsv() {
    if (!resp) return;
    const lines = ['Alumna,Plan,Esperado,Pagado,Resta,Estado,Telefono'];
    for (const s of resp.data) {
      lines.push([
        `"${s.student_name.replace(/"/g, '')}"`, `"${(s.membership_name || '').replace(/"/g, '')}"`,
        s.expected, s.paid, s.remaining, STATUS_BADGE[s.status].label,
        `${s.phone_prefix || ''}${s.phone_number || ''}`,
      ].join(','));
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cobranza-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const s = resp?.summary;
  const collectedPct = s && s.expected_total > 0 ? Math.round((s.collected_total / s.expected_total) * 100) : 0;

  return (
    <div>
      {/* Descuentos de familia a revisar (23-sep-2026). Karla nunca dejó el descuento familiar a quien quedó
          sola; esto lo avisa, no lo quita. */}
      {revFam.length > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm" data-testid="revision-familia">
          <p className="font-semibold text-amber-800 dark:text-amber-300">👪 Descuentos de familia a revisar ({revFam.length})</p>
          <ul className="mt-1 space-y-1 text-content-2">
            {revFam.map((x) => (
              <li key={x.contact_id}>
                <Link to={`/alumnos/${x.contact_id}`} className="font-medium text-content hover:underline">{x.nombre}</Link>
                <span className="text-content-3"> · {x.descuento} · </span>{x.texto}
                {x.familia.length > 0 && <span className="text-content-3"> (familia: {x.familia.map((f) => `${f.nombre}${f.activa ? '' : ' — baja'}`).join(', ')})</span>}
                {x.estado === 'sin_familia' && x.sugerencia && x.sugerencia.length > 0 && (
                  <span className="text-content-3"> · ¿familia de {x.sugerencia.map((f) => f.nombre).join(' o ')}? Confírmalo en su ficha.</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Navegación de mes */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2"><ChevronLeft size={16} /></button>
          <div className="min-w-[9rem] text-center">
            <div className="font-display text-lg font-bold capitalize text-content">{mLabel}</div>
            {isCurrent && <div className="text-[11px] text-content-3">mes en curso</div>}
          </div>
          <button onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={isCurrent}
            className="rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40"><ChevronRight size={16} /></button>
        </div>
        <button onClick={exportCsv} title="Descargar Excel/CSV de cobranza"
          className="rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2"><Download size={16} /></button>
      </div>

      {/* KPIs */}
      {s && (
        <>
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
            <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
              <div className="text-xs text-content-3">Falta por cobrar</div>
              <div className="font-display text-xl font-bold text-amber-600 dark:text-amber-400 sm:text-2xl"><CountUp value={s.pending_total} format={money} /></div>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
              <div className="text-xs text-content-3">Alumnas por cobrar</div>
              <div className="font-display text-xl font-bold text-content sm:text-2xl">
                <CountUp value={s.pending_count} /><span className="text-base font-normal text-content-3"> / {s.billable}</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
              <div className="text-xs text-content-3">Cobrado del mes</div>
              <div className="font-display text-xl font-bold text-emerald-600 dark:text-emerald-400 sm:text-2xl"><CountUp value={s.collected_total} format={money} /></div>
            </div>
          </div>
          {/* Becas del mes (7-oct-2026): fuera de «por cobrar», pero a la vista. */}
          {!!s.scholarship_count && (
            <p className="mb-3 text-xs text-content-3" title={(resp?.scholarships || []).map((b) => `${b.student_name} · ${b.pct}% · ${money(b.forgiven)}`).join('\n')}>
              🎓 {s.scholarship_count} becado{s.scholarship_count > 1 ? 's' : ''} este mes · {money(s.scholarship_total || 0)} sin cobrar por beca:{' '}
              {(resp?.scholarships || []).map((b) => b.student_name).join(', ')}
            </p>
          )}
          {/* Barra cobrado vs esperado */}
          <div className="mb-4">
            <div className="mb-1 flex justify-between text-xs text-content-3">
              <span>Cobrado {money(s.collected_total)} de {money(s.expected_total)}</span><span>{collectedPct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full rounded-full bg-emerald-500 transition-[width]" style={{ width: `${collectedPct}%` }} />
            </div>
          </div>
        </>
      )}

      {/* Filtros */}
      <div className="mb-4 inline-flex flex-wrap gap-1 rounded-lg border border-line bg-surface p-1">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`rounded-md px-3 py-1 text-sm font-medium transition ${filter === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
            {label}{resp && k !== 'todas' && (
              <span className="ml-1 text-xs opacity-70">
                {k === 'pendientes' ? resp.summary.pending_count : k === 'parciales' ? resp.summary.partial_count : resp.summary.paid_count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={filter === 'pendientes' ? CheckCircle2 : PartyPopper}
            title={filter === 'pendientes' ? '¡Todos al corriente!' : 'Sin alumnos en este filtro'}
            subtitle={filter === 'pendientes' ? `Nadie debe la mensualidad de ${mLabel}.` : 'Cambia el filtro para ver más.'} />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          {/* Escritorio: tabla */}
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-line bg-surface-2 text-left text-xs text-content-3">
                <th className="px-4 py-2 font-medium">Alumna</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 text-right font-medium">Mensualidad</th>
                <th className="px-4 py-2 text-right font-medium">Pagado</th>
                <th className="px-4 py-2 font-medium">Estado</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((st) => {
                const link = getWhatsAppLink(st.phone_prefix, st.phone_number, waMessage(st, mLabel));
                return (
                  <tr key={st.account_id} className="border-t border-line transition even:bg-surface-2/40 hover:bg-surface-2">
                    <td className="px-4 py-2">
                      <Link to={`/alumnos/${st.account_id}`} className="flex items-center gap-2 font-medium text-content hover:text-accent-ink">
                        <PersonAvatar contactId={st.account_id} hasPhoto={st.has_photo}
                          name={st.student_name} size="h-7 w-7" />
                        <span className="truncate">{st.student_name}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-content-2">
                      {st.membership_name}
                      {st.discount_name && <span title={st.discount_name} className="ml-1 rounded bg-brand-blue/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-blue">desc.</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-content">
                      {money(st.expected)}
                      {st.agreement && st.list_price != null && st.list_price !== st.expected && (
                        <span className="block text-[11px] text-content-3" title={`Precio de lista ${money(st.list_price)}`}>{st.agreement} · lista {money(st.list_price)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-content-2">
                      {st.paid > 0 ? money(st.paid) : <span className="text-content-3">—</span>}
                      {st.status === 'partial' && <span className="block text-[11px] text-amber-600 dark:text-amber-400">restan {money(st.remaining)}</span>}
                    </td>
                    <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE[st.status].cls}`}>{STATUS_BADGE[st.status].label}</span></td>
                    <td className="px-2 py-2 text-right">
                      {st.status !== 'paid' && link && (
                        <a href={link} target="_blank" rel="noopener noreferrer" title="Recordar por WhatsApp"
                          className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] px-2.5 py-1.5 text-xs font-semibold text-white transition hover:brightness-95">
                          <WhatsAppIcon size={14} /> Cobrar
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Móvil: tarjetas */}
          <div className="divide-y divide-line sm:hidden">
            {rows.map((st) => {
              const link = getWhatsAppLink(st.phone_prefix, st.phone_number, waMessage(st, mLabel));
              return (
                <div key={st.account_id} className="flex items-center gap-3 px-4 py-2.5">
                  <Link to={`/alumnos/${st.account_id}`} className="shrink-0">
                    <PersonAvatar contactId={st.account_id} hasPhoto={st.has_photo}
                      name={st.student_name} size="h-9 w-9" initialClass="text-sm" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/alumnos/${st.account_id}`} className="block truncate font-medium text-content">{st.student_name}</Link>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-content-3">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[st.status].cls}`}>{STATUS_BADGE[st.status].label}</span>
                      <span>{money(st.status === 'partial' ? st.remaining : st.expected)}</span>
                      {st.agreement && <span className="truncate">· {st.agreement}</span>}
                    </div>
                  </div>
                  {st.status !== 'paid' && link && (
                    <a href={link} target="_blank" rel="noopener noreferrer"
                      className="flex shrink-0 items-center gap-1 rounded-lg bg-[#25D366] px-2.5 py-2 text-xs font-semibold text-white">
                      <WhatsAppIcon size={15} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
